import 'apps/pathways/styles/components/preferences.scss';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import withData from 'util/hoc/with-data';
import PrefSvg from 'library/icons/preferences.svg';
import { resolve, set } from 'library/helpers/path-resolver';
import { format_number } from 'library/helpers/numbers';
import { capitalize } from 'library/helpers/text';
import { LocationComponent, GoogleMapsAutocomplete, CustomLocation, FindLocation } from 'library/components/google-maps-api';
import MultiSelect from 'library/components/form-elements/multi-select';
import Query from 'library/util/query';
import Cache from 'library/util/cache';
import { jsonToInputParams } from 'library/util/graph';
import SavingButton from 'library/components/saving-button';
import mergeOptions from 'library/util/merge-options';

const GetChild = (children) => {
  if (children === null || !children) return 'ANY';

  if (typeof children === 'string') {
    return children;
  }

  if (children.length == 0) {
    return 'ANY';
  }

  try {
    return React.Children.only(children);
  } catch (e) {
    return children.length > 1 ? 'Multiple' : children[0];
  }
};

const RenderValue = ({ children }) => (
  <span className="pathways-preferences-control-value">
    { GetChild(children) }
  </span>
);

class Preferences extends React.Component {
  static cache_key = 'opportunity_preferences';

  static defaultProps = {
    throttle: 200,
    onUpdate: ()=>{}
  }

  static propTypes = {
    graph_data: PropTypes.object,
    graph_loading: PropTypes.bool,
    graph_update: PropTypes.func,
    throttle: PropTypes.number,
    onUpdate: PropTypes.func
  }

  state = {
    open: false,
    location: new CustomLocation(),
    formData: {},
    only: 'all'
  }

  constructor(...params) {
    super(...params);

    this.initFilters();
  }

  initFilters = async () => {
    const preferences = (await Cache.get('preferences_filters', 'interface_preferences')) || {only:'all'};

    this.setState({
      only: preferences.only
    });
  }

  static getDerivedStateFromProps({ graph_data:{job_families, ...gd} }, prevState) {
    return {
      formData: mergeOptions(
        gd,
        prevState.formData
      )
    };
  }

  handleChange = (e) => {
    let {
      name,
      value,
      nodeName,
      options,
      multiple
    } = e.target;

    if (nodeName === 'SELECT' && multiple) {
      value = [];
      for (var i = 0, l = options.length; i < l; i++) {
        if (options[i].selected) {
          value.push(options[i].value);
        }
      }
    }

    const nextState = {...this.state};
    nextState.formData[name] = value;

    this.setState({
      formData: nextState.formData
    });
  }

  clear = () => {
    this.setState(
      Preferences.getDerivedStateFromProps(this.props, this.state)
    );
  }

  save = async () => {
    const {
      formData
    } = this.state;

    this.setState({
      saving: true
    });

    this.hasFocused = false;

    const data = {
      ...formData,
      location_name:resolve(formData, 'location/name', null)||null,
      lat:resolve(formData, 'location/lat', null)||null,
      long:resolve(formData, 'location/lng', null)||null
    };

    delete data.location;

    try {
      const res = await Query(`
          mutation {
              update_opportunity_preferences (${jsonToInputParams({input: data})}) {
                opportunity_preferences {
                  education,
                  industry,
                  interests,
                  lat,
                  long,
                  location_name,
                  min_salary
                },
                success,
                errors
              }
          }
      `);

      const resData = {
        account: {
          opportunity_preferences: resolve(res, 'data/update_opportunity_preferences/opportunity_preferences', {})
        },
        job_families: resolve(this.props, 'graph_data/job_families', [])
      };
      await Cache.set(Preferences.cache_key, resData, 'graph_data', 300);
      await this.props.graph_update(resData, true);
    } catch (e) {
      console.error(e);
    }

    try {
      await this.props.onUpdate();
    } catch (e) {
      console.error(e);
    }

    this.setState({
      saving: false,
      open: this.hasFocused
    });
  }

  handleOnFocus = () => {
    this.hasFocused = true;
  }

  setFilter = async (val) => {
    this.setState({
      only: val
    });

    const preferences = (await Cache.get('preferences_filters', 'interface_preferences')) || {};

    preferences.only = val;

    await Cache.set('preferences_filters', preferences, 'interface_preferences');

    this.props.onUpdate();
  }

  render() {
    const {
      graph_loading,
      graph_data
    } = this.props;

    const {
      open,
      formData,
      saving,
      only
    } = this.state;

    if (graph_loading) {
      return null;
    }

    return (
      <div className="pathways-preferences">
        <div className="pathways-preferences-control">
          <div className="pathways-preferences-control-section">
            <span className={['pathways-preferences-control-filter', only==='likes'?'active':null].filter(c=>c).join(' ')} onClick={()=>this.setFilter('likes')}>LIKED</span>
            <span className="pathways-preferences-control-divider">|</span>
            <span className={['pathways-preferences-control-filter', only==='dislikes'?'active':null].filter(c=>c).join(' ')} onClick={()=>this.setFilter('dislikes')}>DISLIKED</span>
            <span className="pathways-preferences-control-divider">|</span>
            <span className={['pathways-preferences-control-filter', only==='all'?'active':null].filter(c=>c).join(' ')} onClick={()=>this.setFilter('all')}>ALL</span>
          </div>
          <div className="pathways-preferences-control-section">
            <label>
              Education:
              <RenderValue>{ capitalize(resolve(graph_data, 'education', null))||'ANY' }</RenderValue>
            </label>
            <label>
              Industry:
              <RenderValue>{ resolve(graph_data, 'industry', null)||'ANY' }</RenderValue>
            </label>
            <label>
              Salary:
              <RenderValue>{ `${format_number(resolve(graph_data, 'min_salary', null))||'ANY'}+` }</RenderValue>
            </label>
            <label>
              Location:
              <RenderValue>
                {
                  resolve(graph_data, 'lat', '') !== '' && resolve(graph_data, 'long', '') !== ''
                    ? <LocationComponent lat={resolve(graph_data, 'lat', '')} lng={resolve(graph_data, 'long', '')} />
                    : 'ANY'
                }
              </RenderValue>
            </label>
            <a href="javascript:void(0)" onClick={()=>this.setState({open:!open})}><PrefSvg style={{transform: 'translateY(.2em)'}} /> EDIT PREFERENCES</a>
          </div>
        </div>
        <div className={['pathways-preferences-drawer', open?'open':null].filter(c=>c).join(' ')}>
          <div className="pathways-preferences-inner">
            <span>
              Minimum Education
              <select onFocus={this.handleOnFocus} name="education" value={resolve(formData, 'education', '')} onChange={this.handleChange}>
                <option value="">No Preference</option>
                <option value="high-school">High School</option>
                <option value="associates">Associates</option>
                <option value="bachelors">Bachelors</option>
                <option value="masters">Masters</option>
                <option value="doctorate">Doctorate</option>
              </select>
            </span>
            <span>
              Desired Location
              <GoogleMapsAutocomplete
                onFocus={this.handleOnFocus}
                placeholder="Use my Current Location"
                value={resolve(formData, 'location', '')}
                name="location"
                onChange={this.handleChange}
                disabled={graph_loading}
              />
            </span>
            <span>
              Desired Salary
              <select onFocus={this.handleOnFocus} name="min_salary" value={resolve(formData, 'min_salary', '')} onChange={this.handleChange}>
                <option value="">Any</option>
                <option value="30000">$30,000+</option>
                <option value="40000">$40,000+</option>
                <option value="50000">$50,000+</option>
                <option value="60000">$60,000+</option>
                <option value="70000">$70,000+</option>
                <option value="80000">$80,000+</option>
              </select>
            </span>
            <span>
              Desired Industries
              <MultiSelect onFocus={this.handleOnFocus} name="industry" value={resolve(formData, 'industry', '')} onChange={this.handleChange}>
                {
                  resolve(graph_data, 'job_families', []).map(({ id, title })=>(
                    <option key={id} value={title}>{title}</option>
                  ))
                }
              </MultiSelect>
            </span>
            <div>
              <span>
                <button onClick={this.clear} className="button-tertiary">RESET</button>
                <SavingButton saving={saving} onClick={this.save}>SAVE</SavingButton>
              </span>
              <a onClick={()=>this.setState({open:false})} href="javascript:void(0)">close</a>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

Preferences.parseQueryData = async (d) => {
  let out = {
    ...resolve(d, 'account/opportunity_preferences', {}),
    job_families: resolve(d, 'job_families', [])
  };


  out = set(out, 'location', new CustomLocation(
    resolve(d, 'account/opportunity_preferences/location_name', ''),
    resolve(d, 'account/opportunity_preferences/lat', null),
    resolve(d, 'account/opportunity_preferences/long', null),
  ));

  return out;
};
Preferences.query = async (CancelToken) => {
  const { data } = await Query(`
    {
      account {
        opportunity_preferences {
          education,
          industry,
          interests,
          lat,
          long,
          location_name,
          min_salary
        }
      },
      job_families {
        id
        title
      }
    }
  `);

  if (!resolve(data, 'account/opportunity_preferences/location_name', '')) {
    try {
      const { location } = await FindLocation({lat:resolve(data, 'account/opportunity_preferences/lat', 0), lng:resolve(data, 'account/opportunity_preferences/long', 0)}, CancelToken);
      await Query(`
        mutation {
          update_opportunity_preferences (input: {location_name: '${location.name}'}) {
            success
          }
        }
      `);
      set(data, 'account/opportunity_preferences/location_name', location);
    } catch (e) {
      console.error('Error!', e);
    }
  }

  return {
    data: data
  };

};

export default withData(Preferences, __LANGUAGE__, false);
